/**
 * Nine stages down the left, one shown at a time.
 *
 * Numbered, because this is a sequence and a new employee should be able to see
 * that before reading a word of it. Each carries how many blocking things are
 * still open, so the list answers "where is the work" without opening anything.
 *
 * Buttons in a list, not links: the stage is view state, not a location, and
 * making it a route would mean nine URLs that mean nothing to anybody who has
 * not read this file. The two entries below the rule are reference material
 * rather than steps, which is why they sit apart and are not numbered.
 */
import page from '../../../shared/Page.module.css';
import own from './Onboarding.module.css';
import { STAGE_STATUS_LABEL, type Stage, type StageId } from './types';

export type View = StageId | 'intake' | 'activity';

/** Stages that total the others rather than holding requirements of their own. */
const SUMMARY_STAGES = new Set<StageId>(['readiness', 'goLive']);

export function StageNav({
  stages, current, onSelect,
}: {
  stages: Stage[];
  current: View;
  onSelect: (view: View) => void;
}) {
  const extras: { id: View; label: string }[] = [
    { id: 'intake', label: 'Customer intake' },
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <nav aria-label="Onboarding stages" className={own.navWrap}>
      <ul className={own.nav}>
        {stages.map((stage, index) => {
          const on = current === stage.id;
          return (
            <li key={stage.id}>
              <button
                type="button"
                className={`${own.navItem} ${on ? own.navItemOn : ''}`}
                aria-current={on ? 'step' : undefined}
                onClick={() => onSelect(stage.id)}
              >
                <span className={own.navNumber} aria-hidden="true">{index + 1}</span>
                <span className={own.navLabel}>{stage.title}</span>
                <span className="vh">, {STAGE_STATUS_LABEL[stage.status]}</span>
                {/*
                  * SUMMARY STAGES CARRY NO COUNT.
                  *
                  * Readiness and Go live hold no requirements of their own —
                  * they total every other stage's. Showing that total in the
                  * same column made the list read 3, 2, 4, 1, 1, 1, 12, 12, so
                  * anybody adding it up got a number three times the truth.
                  */}
                {SUMMARY_STAGES.has(stage.id) ? (
                  stage.status === 'complete'
                    ? <span className={`${own.navCount} ${own.navCountDone}`} aria-hidden="true">✓</span>
                    : null
                ) : stage.blockingOpen > 0 ? (
                  <span className={own.navCount} aria-hidden="true">{stage.blockingOpen}</span>
                ) : stage.status === 'complete' ? (
                  <span className={`${own.navCount} ${own.navCountDone}`} aria-hidden="true">✓</span>
                ) : null}
              </button>
            </li>
          );
        })}

        <li><hr className={own.navDivider} /></li>
        {extras.map((extra) => (
          <li key={extra.id}>
            <button
              type="button"
              className={`${own.navItem} ${current === extra.id ? own.navItemOn : ''}`}
              aria-current={current === extra.id ? 'true' : undefined}
              onClick={() => onSelect(extra.id)}
            >
              <span className={own.navNumber} aria-hidden="true" />
              <span className={own.navLabel}>{extra.label}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className={page.itemMeta} style={{ marginTop: 'var(--sp-3)' }}>
        A number is how many things still block go-live in that stage.
      </p>
    </nav>
  );
}
